/**
 * Exchange Rates Tab - Currency rates management
 */

import React, { useState, useEffect } from 'react';
import { RefreshCw, Clock } from 'lucide-react';
import { useLanguage } from '../../../i18n';
import { apiGetAllExchangeRates, apiBulkSaveExchangeRates, ExchangeRate } from '../../../services/api';

const ExchangeRatesTab: React.FC = () => {
  const { t, language } = useLanguage();

  const [exchangeRates, setExchangeRates] = useState<ExchangeRate[]>([]);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [ratesError, setRatesError] = useState<string | null>(null);
  const [lastRateUpdate, setLastRateUpdate] = useState<Date | null>(null);

  useEffect(() => {
    loadExchangeRates();
  }, []);

  const loadExchangeRates = async () => {
    setIsLoadingRates(true);
    setRatesError(null);
    try {
      const rates = await apiGetAllExchangeRates();
      setExchangeRates(rates);
      if (rates.length > 0) {
        const latest = rates.reduce((a, b) =>
          new Date(a.fetched_at) > new Date(b.fetched_at) ? a : b
        );
        setLastRateUpdate(new Date(latest.fetched_at));
      }
    } catch (error: unknown) {
      setRatesError(error instanceof Error ? error.message : String(error));
    } finally {
      setIsLoadingRates(false);
    }
  };

  const fetchLatestRates = async () => {
    setIsLoadingRates(true);
    setRatesError(null);
    try {
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
      const data = await response.json();

      const currencies = [
        'EUR', 'GBP', 'TRY', 'JPY', 'CNY', 'CAD', 'AUD',
        'AED', 'SAR', 'MXN', 'INR', 'BRL', 'PLN', 'SEK',
        'SGD', 'ZAR', 'EGP', 'NZD', 'CHF', 'HKD'
      ];
      const rates = currencies
        .filter(code => data.rates[code])
        .map(code => ({
          currency_code: code,
          currency_name: getCurrencyName(code),
          rate_to_usd: 1 / data.rates[code]
        }));

      rates.unshift({
        currency_code: 'USD',
        currency_name: 'US Dollar',
        rate_to_usd: 1
      });

      await apiBulkSaveExchangeRates(rates, 'api');
      await loadExchangeRates();
    } catch (error: unknown) {
      setRatesError(error instanceof Error ? error.message : String(error));
    } finally {
      setIsLoadingRates(false);
    }
  };

  const getCurrencyName = (code: string): string => {
    const names: Record<string, string> = {
      USD: 'US Dollar',
      EUR: 'Euro',
      GBP: 'British Pound',
      TRY: 'Turkish Lira',
      JPY: 'Japanese Yen',
      CNY: 'Chinese Yuan',
      CAD: 'Canadian Dollar',
      AUD: 'Australian Dollar',
      AED: 'UAE Dirham',
      SAR: 'Saudi Riyal',
      MXN: 'Mexican Peso',
      INR: 'Indian Rupee',
      BRL: 'Brazilian Real',
      PLN: 'Polish Zloty',
      SEK: 'Swedish Krona',
      SGD: 'Singapore Dollar',
      ZAR: 'South African Rand',
      EGP: 'Egyptian Pound',
      NZD: 'New Zealand Dollar',
      CHF: 'Swiss Franc',
      HKD: 'Hong Kong Dollar'
    };
    return names[code] || code;
  };

  return (
    <div className="space-y-6">
      {/* Header with refresh button */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-slate-700">{t.settings.currentRates}</h4>
          <p className="text-xs text-slate-500 mt-1">{t.settings.ratesNote}</p>
        </div>
        <button
          onClick={fetchLatestRates}
          disabled={isLoadingRates}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isLoadingRates ? 'animate-spin' : ''}`} />
          {t.settings.updateRates}
        </button>
      </div>

      {ratesError && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg text-sm">
          {ratesError}
        </div>
      )}

      {/* Last update info */}
      {lastRateUpdate && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Clock className="w-4 h-4" />
          <span>
            {t.settings.lastUpdate}
            {lastRateUpdate.toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US')}
          </span>
        </div>
      )}

      {/* Exchange rates table */}
      <div className="bg-slate-50 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-100">
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">
                {t.settings.currency}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">
                {t.settings.name}
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase">
                1 {t.settings.unit} = USD
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase">
                1 USD =
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">
                {t.settings.source}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {exchangeRates.map(rate => (
              <tr key={rate.currency_code} className="hover:bg-slate-100">
                <td className="px-4 py-3 font-mono font-medium text-slate-800">
                  {rate.currency_code}
                </td>
                <td className="px-4 py-3 text-slate-600">
                  {rate.currency_name}
                </td>
                <td className="px-4 py-3 text-right font-mono text-slate-800">
                  ${Number(rate.rate_to_usd).toFixed(6)}
                </td>
                <td className="px-4 py-3 text-right font-mono text-slate-600">
                  {rate.rate_to_usd > 0 ? (1 / Number(rate.rate_to_usd)).toFixed(4) : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-slate-500">
                  {rate.source}
                </td>
              </tr>
            ))}
            {exchangeRates.length === 0 && !isLoadingRates && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  {t.settings.noRatesYet}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExchangeRatesTab;
